import { useState, useEffect } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import ReportTable from "../../components/reports/cards/ReportCard";
import { CTBReport, CTBReportAPIResponse, parseReport } from "../../utils/api/endpoints/reports/parseReports";
import ReportQuickView from "../../components/reports/ReportQuickView";
import useReports from "../../utils/hooks/reports/useReports";
import usePageTitle from "../../utils/hooks/usePageTitle";
import useFilterOptions from "../../utils/hooks/reports/useFilter";
import LoadingSpinner from "../../components/retests/utils/LoadingSpinner";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import { useGetMyAcceptedDelegationsQuery } from "../../utils/api/endpoints/delegationsApi";
import { getRetestStatus } from "../../utils/api/endpoints/retests/retests";
import { ReportFilters } from "../../utils/api/endpoints/reportsApi";

// Load filters from local storage (excluding page)
const loadFiltersFromLocalStorage = () => {
  const savedFilters = localStorage.getItem("reportFilters");
  if (savedFilters) {
    const parsed = JSON.parse(savedFilters);
    return parsed;
  }
  
  return {
    searchQuery: "",
    program: "",
    reportState: "",
    severityCategory: "",
    vulnerabilityType: "",
    sortOrder: "desc",
    pentesterUsername: "",
    includeArchived: "false",
    includeClosed: false
  };
};

const PaginatedReports = () => {
  const [selectedReport, setSelectedReport] = useState<CTBReport | undefined>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize filters (excluding page, as it's managed via URL)
  const [filters, setFilters] = useState(() => {
    const base = loadFiltersFromLocalStorage();
    const qp = new URLSearchParams(window.location.search);
    const programId = qp.get("programId");
    const programName = qp.get("programName");
    if (programId || programName) {
      return { ...base, program: programId || programName || "" };
    }
    return base;
  });

  // Get current page from URL, default to 1
  const getCurrentPage = () => {
    const urlPage = parseInt(searchParams.get("page") || "1");
    return !isNaN(urlPage) && urlPage > 0 ? urlPage : 1;
  };

  const currentPage = getCurrentPage();

  // Clear pagination state when navigating away from reports
  useEffect(() => {
    const isReportsPath = location.pathname.startsWith("/dashboard/reports");
    if (!isReportsPath) {
      setSearchParams(prev => {
        prev.delete("page");
        return prev;
      });
    }
  }, [location.pathname, setSearchParams]);

  const {
    reports,
    count,
    isLoading,
    isError,
    setPage,
    setFilters: setReportFilters
  } = useReports({ ...filters, limit: 7, page: currentPage });

  const {
    filterOptions,
    loading: filterOptionsLoading,
    error: filterOptionsError
  } = useFilterOptions();

  usePageTitle("Reports");
  const { role } = useUserCredentials();
  
  // Role-based default filters for Admin and QA roles
  useEffect(() => {
    // Only apply default filters if none are saved in localStorage
    const savedFilters = localStorage.getItem("reportFilters");
    
    if (!savedFilters && role && [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER, UserRole.QA].includes(role)) {
      setFilters((prev: ReportFilters) => {
        const updated = {
          ...prev,
          includeArchived: "true",
          includeClosed: true
        };
        // Also update backend filters immediately
        setReportFilters({ ...updated, limit: 7, page: currentPage });
        return updated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // Researcher-only tab: My vs Delegated
  const [delegatedReportsWithStatus, setDelegatedReportsWithStatus] = useState<CTBReport[]>([]);
  const [activeTab, setActiveTab] = useState<"mine" | "delegated">(
    role === UserRole.RESEARCHER && searchParams.get("view") === "delegated" ? "delegated" : "mine"
  );

  useEffect(() => {
    if (role !== UserRole.RESEARCHER) return;

    // If delegated reports are empty and user is on delegated tab, switch to mine
    if (delegatedReportsWithStatus.length === 0 && activeTab === "delegated") {
      setActiveTab("mine");
      setSearchParams(prev => {
        prev.delete("view");
        return prev;
      });
      return;
    }

    setSearchParams(prev => {
      if (activeTab === "delegated") prev.set("view", "delegated");
      else prev.delete("view");
      return prev;
    });
  }, [activeTab, role, setSearchParams, delegatedReportsWithStatus]);

  // Delegated reports for researcher
  const { data: acceptedReportsResp, isFetching: loadingDelegated } = useGetMyAcceptedDelegationsQuery(
    { scope: "report_comment" },
    { skip: role !== UserRole.RESEARCHER }
  );

  // Fetch retest status for delegated reports
  useEffect(() => {
    const fetchRetestStatusForDelegated = async () => {
      const items = acceptedReportsResp?.data || [];
      const reportsWithStatus = await Promise.all(
        items.map(async (d: any) => {
          const rep = d.Report;
          if (!rep) return null;

          const api: CTBReportAPIResponse = {
            report_id: rep.id ?? rep.report_id,
            user_id: rep.user_id,
            program_id: rep.program_id,
            is_delete: rep.is_delete ?? false,
            report_title: rep.report_title,
            submitted_date: rep.submitted_date,
            severity_category: rep.severity_category,
            state: rep.state,
            triage_status: rep.triage_status
          } as any;

          const parsedReport = parseReport(api);

          // Fetch retest status if report exists
          try {
            const retestStatus = await getRetestStatus(parsedReport.id);
            if (retestStatus) {
              parsedReport.retestStatus = retestStatus;
            }
          } catch (err) {
            // If no retest status found, just use the report as is
            console.debug("No retest status for report:", parsedReport.id);
          }

          return parsedReport;
        })
      );

      setDelegatedReportsWithStatus(reportsWithStatus.filter(Boolean) as CTBReport[]);
    };

    if (role === UserRole.RESEARCHER && acceptedReportsResp?.data) {
      fetchRetestStatusForDelegated();
    }
  }, [acceptedReportsResp, role]);

  const delegatedReports = delegatedReportsWithStatus;

  // Handle page change
  const handlePageChange = (page: number) => {
    setSearchParams(prev => {
      prev.set("page", page.toString());
      return prev;
    });
    setPage(page);
  };

  // Handle filter changes (resets page to 1)
  const handleFilter = (newFilters: any) => {
    const updatedFilters = { ...newFilters };
    setFilters(updatedFilters);
    localStorage.setItem("reportFilters", JSON.stringify(updatedFilters));
    setReportFilters({ ...updatedFilters, limit: 7, page: 1 });
    setSearchParams(prev => {
      prev.set("page", "1");
      return prev;
    });
  };

  // If deep-linked with programId/programName, ensure backend filters get applied once
  useEffect(() => {
    const qp = new URLSearchParams(window.location.search);
    const programId = qp.get("programId");
    const programName = qp.get("programName");
    if (programId || programName) {
      const nf = { ...filters, program: programId || programName || "" } as any;
      setFilters(nf);
      localStorage.setItem("reportFilters", JSON.stringify(nf));
      setReportFilters({ ...nf, limit: 7, page: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle refresh for bulk actions
  const handleRefresh = () => {
    // Refetch reports data
    setReportFilters({ ...filters, limit: 7, page: currentPage });
  };

  if (filterOptionsLoading) {
    return <LoadingSpinner />;
  }

  if (filterOptionsError || !filterOptions) {
    return (
      <div>Error loading filter options: {filterOptionsError?.message}</div>
    );
  }

  return (
    <div className="montserrat-font relative flex h-full flex-col px-4">
      {/* Reports Title - Outside the container */}
      <div className="mt-6 mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
        {role === UserRole.RESEARCHER && (
          <button
            onClick={() => navigate("/dashboard/reports/new")}
            className="inline-flex items-center rounded-md px-3 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            style={{ backgroundColor: "#010D2C" }}
          >
            Create Report
          </button>
        )}
      </div>

      {role === UserRole.RESEARCHER && delegatedReportsWithStatus.length > 0 && (
        <div className="mb-2 border-b border-gray-200">
          <nav className="-mb-px flex space-x-6">
            <button
              className={`whitespace-nowrap border-b-2 px-1 pb-2 text-sm font-medium ${
                activeTab === "mine" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("mine")}
            >
              My Reports
            </button>
            <button
              className={`whitespace-nowrap border-b-2 px-1 pb-2 text-sm font-medium ${
                activeTab === "delegated" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("delegated")}
            >
              Delegated Reports
            </button>
          </nav>
        </div>
      )}

      <div className="flex flex-grow overflow-auto">
        <div className="flex-grow">
          {role === UserRole.RESEARCHER && activeTab === "delegated" ? (
            loadingDelegated ? (
              <LoadingSpinner />
            ) : (
              <ReportTable
                reports={delegatedReports}
                filterOptions={filterOptions}
                filters={filters}
                onFilter={handleFilter}
                onClick={report => navigate(`/dashboard/reports/${report.id}`)}
                count={delegatedReports.length}
                currentPage={1}
                onPageChange={() => {}}
                onRefresh={handleRefresh}
                hideFilters={true}
              />
            )
          ) : isLoading ? (
            <LoadingSpinner />
          ) : isError ? (
            <div>
              <ReportTable
                reports={[]}
                filterOptions={filterOptions}
                filters={filters}
                onFilter={handleFilter}
                onClick={() => {}}
                count={0}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onRefresh={handleRefresh}
              />
            </div>
          ) : (
            <ReportTable
              reports={reports}
              filterOptions={filterOptions}
              filters={filters}
              onFilter={handleFilter}
              onClick={report => {
                // Store the current page in localStorage before navigating
                localStorage.setItem(
                  "reportsCurrentPage",
                  currentPage.toString()
                );
                // Navigate directly to the report detail page
                navigate(`/dashboard/reports/${report.id}`);
              }}
              count={count}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onRefresh={handleRefresh}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PaginatedReports;
